import React, { useState } from "react";
import { CalendarDays, Download } from "lucide-react";
import api, { fmtINRFull, formatApiError } from "../../lib/api";
import { formatTimelineDate } from "../../lib/paymentTimeline";
import { useToast } from "../../lib/toast";
import "./PaymentTimeline.css";

// Booking payment schedule with the exact calendar date of every instalment,
// plus a PDF copy the customer can keep. Rows come from BookingFlow:
// { label, amount, due_date, dateText, relative }.
export default function PaymentTimeline({
  rows, eventDate, artistName, packageName, city, total,
  title = "Payment timeline", compact = false, testId = "payment-timeline",
}) {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const eventText = formatTimelineDate(eventDate);
  const allDated = rows.every((row) => row.due_date);

  const download = async () => {
    setDownloading(true);
    try {
      const response = await api.post("/payment-timeline/pdf", {
        artist_name: artistName || "",
        package_name: packageName || "",
        city: city || "",
        event_date: eventDate,
        total: Number(total) || rows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
        rows: rows.map((row) => ({ label: row.label, due_date: row.due_date, relative: row.relative || "", amount: Number(row.amount || 0) })),
      }, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `booktalent-payment-timeline-${eventDate || "booking"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast(formatApiError(error) || "Could not create the PDF", "error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`payment-timeline ${compact ? "is-compact" : ""}`} data-testid={testId}>
      <div className="payment-timeline-head">
        <div className="fw-700">{title}</div>
        <div className="text-muted fs-11">
          {eventText ? `Exact payment dates for your event on ${eventText}.` : "Pick an event date to see the exact payment dates."}
        </div>
      </div>
      <ol className="payment-timeline-list">
        {rows.map((row, index) => {
          const dateText = row.dateText || formatTimelineDate(row.due_date);
          return (
            <li key={`${row.label}-${index}`} className="payment-timeline-row" data-testid={`${testId}-row-${index}`}>
              <span className="payment-timeline-dot" aria-hidden="true" />
              <div className="payment-timeline-body">
                <div className="payment-timeline-label">{row.label}</div>
                <div className="payment-timeline-date">
                  <CalendarDays size={12} aria-hidden="true" />
                  {dateText || <span className="text-muted">Date set once you pick the event date</span>}
                </div>
                {row.relative && <div className="payment-timeline-relative">{row.relative}</div>}
              </div>
              <strong className="payment-timeline-amount">{fmtINRFull(row.amount)}</strong>
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        className="btn btn-ghost btn-sm payment-timeline-download"
        onClick={download}
        disabled={downloading || !allDated}
        title={allDated ? "Download this schedule as a PDF" : "Pick an event date first"}
        data-testid={`${testId}-download`}
      >
        <Download size={14} aria-hidden="true" /> {downloading ? "Preparing PDF…" : "Download timeline (PDF)"}
      </button>
    </div>
  );
}
